import { useCallback, useEffect, useMemo, useState } from 'react';
// `Infinity` con alias: el ícono de lucide se llama igual que el global de
// JavaScript y lo tapa dentro de este archivo. ESLint lo marca como error, y con
// razón — nadie espera que `Infinity` sea un componente.
import { Infinity as IconoInfinito, Loader2, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { adminService } from '../services/admin-service';
import type { ModulosDeEmpresa } from '../types/admin.types';

/**
 * Qué módulos compró esta empresa.
 *
 * Es la pantalla que resuelve el pedido: *"si a una empresa le vendemos solo
 * Comercial y Trade Intelligence, el otro módulo no tiene que ver"*.
 *
 * **Casilla y no tres estados.** A nivel plataforma la pregunta es binaria:
 * ¿está incluido o no? Los tres estados —habilitado, con candado, oculto— siguen
 * viviendo donde ya vivían: la matriz de roles de la propia empresa, que es otra
 * decisión y de otra persona.
 *
 * **Agrupado y no una lista plana**, porque son 24 módulos y el agrupado viene
 * del servidor: así hay una sola definición y agregar un módulo no obliga a
 * tocar dos repos.
 */

interface TenantModulesPanelProps {
  tenantId: string;
}

export function TenantModulesPanel({ tenantId }: TenantModulesPanelProps) {
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const [datos, setDatos] = useState<ModulosDeEmpresa | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  /** Lo que el usuario tiene marcado ahora, antes de guardar. */
  const [marcados, setMarcados] = useState<Set<string>>(new Set());

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const respuesta = await adminService.getTenantModules(tenantId);
      setDatos(respuesta);
      setMarcados(new Set(respuesta.modules));
    } catch (err: any) {
      /*
        El error se muestra en la pantalla y no solo en un toast que se va: si la
        consulta falla, dejar la pantalla vacía hace pensar que la empresa no
        tiene módulos, que es justo lo contrario de lo que pasó.
      */
      setError(err?.response?.data?.error ?? 'No se pudieron leer los módulos de esta empresa.');
    } finally {
      setCargando(false);
    }
  }, [tenantId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const original = useMemo(() => new Set(datos?.modules ?? []), [datos]);

  /*
    Qué módulos quedan incluidos DE VERDAD para un conjunto marcado, contando lo
    que arrastran los alias. Es la única comparación honesta: marcar Datos
    Maestros incluye el Tarifario, y comparar listas crudas haría decir "se
    agrega" o "se quita" a algo que no cambia.
  */
  const expandir = useCallback(
    (conjunto: Set<string>): Set<string> => {
      if (!datos) return new Set(conjunto);
      const efectivo = new Set(conjunto);
      for (const grupo of datos.catalog) {
        for (const item of grupo.items) {
          if ((item.impliedBy ?? []).some((o) => conjunto.has(o.key))) efectivo.add(item.key);
        }
      }
      return efectivo;
    },
    [datos]
  );

  const originalEfectivo = useMemo(() => expandir(original), [expandir, original]);
  const marcadosEfectivo = useMemo(() => expandir(marcados), [expandir, marcados]);

  const huboCambios = useMemo(() => {
    if (marcados.size !== original.size) return true;
    for (const m of marcados) if (!original.has(m)) return true;
    return false;
  }, [marcados, original]);

  /**
   * Los que se le quitan de verdad respecto de lo que tenía.
   *
   * Se descuentan los que quedan arrastrados por otro módulo marcado: desmarcar
   * el Tarifario con Datos Maestros incluido no le quita nada a nadie, y avisar
   * que sí sería avisar de algo que no pasa.
   */
  const seQuitan = useMemo(
    () => [...originalEfectivo].filter((m) => !marcadosEfectivo.has(m)),
    [marcadosEfectivo, originalEfectivo]
  );

  const alternar = (key: string) => {
    setMarcados((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(key)) siguiente.delete(key);
      else siguiente.add(key);
      return siguiente;
    });
  };

  const alternarGrupo = (keys: string[], marcarTodo: boolean) => {
    setMarcados((prev) => {
      const siguiente = new Set(prev);
      for (const k of keys) {
        if (marcarTodo) siguiente.add(k);
        else siguiente.delete(k);
      }
      return siguiente;
    });
  };

  const aplicar = async () => {
    setGuardando(true);
    try {
      const res = await adminService.setTenantModules(tenantId, [...marcados]);
      // El backend explica el caso de la lista vacía mejor que un "guardado".
      toast({ title: res?.message ?? 'Módulos guardados' });
      await cargar();
    } catch (err: any) {
      toast({
        title: 'No se pudo guardar',
        description: err?.response?.data?.error ?? 'Intentá de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const guardar = () => {
    if (!datos) return;

    /*
      Se pide confirmación solo cuando se QUITAN módulos, que es lo que corta
      acceso. Agregar no rompe nada, y pedir confirmación para todo entrena a la
      gente a apretar "sí" sin leer.
    */
    if (seQuitan.length === 0) {
      void aplicar();
      return;
    }

    const nombres = seQuitan
      .map((k) => etiquetaDe(datos, k))
      .slice(0, 6)
      .join(', ');
    const resto = seQuitan.length > 6 ? ` y ${seQuitan.length - 6} más` : '';

    /*
      La consecuencia, con el número real de personas afectadas.

      Decía "sus usuarios van a dejar de ver...", que no mueve a nadie: "sus
      usuarios" es una abstracción. "4 personas pierden el acceso" es gente que
      mañana abre el sistema y no puede trabajar. El dato ya viene del servidor.
    */
    const afectados = datos.tenant._count?.users ?? 0;
    const cuantos =
      afectados > 0
        ? `${afectados} ${afectados === 1 ? 'persona' : 'personas'} de ${datos.tenant.name}`
        : `Los usuarios de ${datos.tenant.name}`;

    confirm({
      title: `¿Quitarle ${seQuitan.length} ${seQuitan.length === 1 ? 'módulo' : 'módulos'} a ${datos.tenant.name}?`,
      description: `${cuantos} dejan de ver ${nombres}${resto} apenas guardes, y la API empieza a rechazarles esas llamadas. Queda registrado quién lo hizo.`,
      confirmText: 'Sí, quitar',
      variant: 'destructive',
      onConfirm: aplicar,
    });
  };

  if (cargando) {
    return (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
        <p className="text-sm font-medium text-destructive">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={cargar}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (!datos) return null;

  const sinTecho = datos.modules.length === 0;

  return (
    <div className="space-y-5">
      <ConfirmDialog />

      {/* ── Qué rige hoy ── */}
      <div className="rounded-lg border bg-muted/30 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              {datos.plan ? (
                <>
                  <span>Plan {datos.plan.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {datos.plan.tier}
                  </Badge>
                </>
              ) : (
                <span>Sin plan asignado</span>
              )}
            </div>
            {datos.endDate && (
              <p className="text-xs text-muted-foreground">
                Vigente hasta {new Date(datos.endDate).toLocaleDateString('es-BO')}
              </p>
            )}
          </div>

          {/*
            Lo más importante de esta tarjeta. "Sin techo" no significa "sin
            acceso": significa que no se está filtrando nada. Es el estado de
            todas las empresas hoy, y se lee al revés si no se dice.
          */}
          {sinTecho ? (
            /*
              🔴 Esto decía "Sin techo configurado", y la gente lo leía al revés.

              Con las 24 casillas vacías, "sin configurar" se entiende como "esta
              empresa no tiene nada" — que es exactamente lo contrario de lo que
              pasa: sin techo, la empresa ve TODO lo que sus roles le permitan.

              El arreglo no es explicar mejor: es **nombrar el modo**. "Acceso total"
              describe lo que hay; "sin configurar" describía lo que falta. Un estado
              con nombre no se puede leer al revés.
            */
            <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
                <IconoInfinito className="h-4 w-4 text-amber-600" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="etiqueta-caps text-amber-700 dark:text-amber-500">Modo actual</p>
                <p className="font-semibold">Acceso total · sin límite</p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Esta empresa ve <strong>todos</strong> los módulos que sus roles le permitan,
                  salvo los productos por contrato (como el Pricer Datec), que tienen que estar
                  marcados. Al marcar la primera casilla empieza a estar limitada a lo que marques.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{datos.modules.length} módulos</span>{' '}
              incluidos ·{' '}
              {datos.origin === 'plan' ? 'los trae el plan' : 'ajustados para esta empresa'}
            </p>
          )}
        </div>
      </div>

      {/* ── El catálogo, por grupo ── */}
      {datos.catalog.map((grupo) => {
        const keys = grupo.items.map((i) => i.key);
        /*
          Cuentan también los que van arrastrados por otro módulo: si no, el
          contador diría "3 de 4" cuando en la práctica van los cuatro.
        */
        const vaIncluido = (item: { key: string; impliedBy?: { key: string }[] }) =>
          marcados.has(item.key) || (item.impliedBy ?? []).some((o) => marcados.has(o.key));
        const incluidos = grupo.items.filter(vaIncluido).length;
        const todos = incluidos === keys.length;

        return (
          <section key={grupo.titulo} className="space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {grupo.titulo}
                </h3>
                {grupo.detalle && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{grupo.detalle}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs tabular-nums text-muted-foreground">
                  {incluidos} de {keys.length}
                </span>
                {/*
                  Marcar un grupo entero de un golpe: con 24 módulos, hacerlo de a
                  uno es donde se cometen los errores.
                */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => alternarGrupo(keys, !todos)}
                >
                  {todos ? 'Quitar todos' : 'Incluir todos'}
                </Button>
              </div>
            </div>

            <div className="grid gap-2 rounded-lg border p-3 sm:grid-cols-2">
              {grupo.items.map((item) => {
                const marcado = marcados.has(item.key);

                /*
                  Si otro módulo marcado lo arrastra, este va incluido igual: el
                  techo se expande por alias antes de aplicarse. Desmarcarlo no
                  tendría efecto, así que se muestra incluido y se explica por
                  qué — dejar una casilla que parece hacer algo y no hace nada es
                  peor que no ofrecerla.
                */
                const arrastradoPor = (item.impliedBy ?? []).filter((o) => marcados.has(o.key));
                const vaIgual = arrastradoPor.length > 0;

                const iba = originalEfectivo.has(item.key);
                const va = marcado || vaIgual;
                const seVaAQuitar = iba && !va;
                const seVaAAgregar = !iba && va;

                return (
                  <label
                    key={item.key}
                    className={`flex items-center gap-2 rounded p-1.5 ${vaIgual ? 'opacity-90' : 'cursor-pointer hover:bg-muted/50'}`}
                  >
                    <Checkbox
                      checked={marcado || vaIgual}
                      disabled={vaIgual}
                      onCheckedChange={() => alternar(item.key)}
                      aria-label={
                        vaIgual
                          ? `${item.label} — incluido por ${arrastradoPor.map((o) => o.label).join(', ')}`
                          : `Incluir ${item.label}`
                      }
                    />
                    <span className="flex-1 text-sm">
                      {item.label}
                      {vaIgual && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          lo incluye {arrastradoPor.map((o) => o.label).join(', ')}
                        </span>
                      )}
                    </span>
                    {/*
                      Se marca lo que cambió respecto de lo guardado, para que al
                      apretar Guardar nadie se lleve una sorpresa.
                    */}
                    {seVaAQuitar && (
                      <Badge variant="destructive" className="text-[10px]">
                        se quita
                      </Badge>
                    )}
                    {seVaAAgregar && (
                      <Badge variant="secondary" className="text-[10px]">
                        se agrega
                      </Badge>
                    )}
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* ── Guardar ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <p className="text-xs text-muted-foreground">
          {seQuitan.length > 0
            ? `Al guardar, ${seQuitan.length === 1 ? 'un módulo deja' : `${seQuitan.length} módulos dejan`} de estar disponible${seQuitan.length === 1 ? '' : 's'} para sus usuarios.`
            : 'Quitar un módulo corta el acceso de sus usuarios, en el menú y en la API.'}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setMarcados(new Set(datos.modules))}
            disabled={!huboCambios || guardando}
          >
            <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Descartar cambios
          </Button>
          <Button onClick={guardar} disabled={!huboCambios || guardando}>
            {guardando ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="mr-1.5 h-4 w-4" aria-hidden="true" />
            )}
            Guardar módulos
          </Button>
        </div>
      </div>
    </div>
  );
}

/** La etiqueta legible de un módulo, buscándola en el catálogo. */
function etiquetaDe(datos: ModulosDeEmpresa, key: string): string {
  for (const grupo of datos.catalog) {
    const item = grupo.items.find((i) => i.key === key);
    if (item) return item.label;
  }
  return key;
}

export default TenantModulesPanel;
